/**
 * Hard fence against payment card numbers (and OAuth codes, below) reaching PostHog.
 *
 * The redeem-code field is free text, and on 2026-09-10 a user typed a Visa
 * PAN into it — captured verbatim as `redeem_code_submitted.code`. Guarding at
 * the call site would need re-applying every time someone adds a property, so
 * this runs in the `before_send` chain instead (posthog-init.ts), where it sees
 * every event.
 *
 * Wrong codes that are NOT card-shaped stay raw on purpose: the rejected
 * strings are what distinguish a mistyped code from one minted on another
 * platform, and that diagnosis is worth keeping.
 */
import type { CaptureResult } from 'posthog-js';

/** Free-text properties a person can type into. Cheap allowlist — this runs on every event. */
const USER_TYPED_KEYS = ['code'] as const;

const PAN_REDACTED = '[redacted:pan]';

/**
 * Whop's OAuth handoff lands on `/auth/whop-callback?code=…`. The code is
 * single-use and spent within seconds, but it is still an auth credential, and
 * the full URL rides on every event of that page (and as `$referrer` on the
 * next one). Replay and pageview capture run before the page could rewrite its
 * own URL, so the fence lives here.
 */
const OAUTH_CALLBACK_PATH = '/auth/whop-callback';
const URL_KEYS = [
  '$current_url',
  '$referrer',
  '$session_entry_url',
  '$session_entry_referrer',
  '$initial_current_url',
  '$initial_referrer',
] as const;
const CODE_REDACTED = 'redacted';

export function redactOAuthCode(value: string): string {
  if (!value.includes(OAUTH_CALLBACK_PATH) || !value.includes('code=')) return value;
  try {
    const url = new URL(value);
    if (url.pathname !== OAUTH_CALLBACK_PATH || !url.searchParams.has('code')) return value;
    url.searchParams.set('code', CODE_REDACTED);
    return url.toString();
  } catch {
    return value;
  }
}

/**
 * A form submitted before hydration falls back to a native GET and puts its
 * fields in the query string: `/sign-in?email=…&password=…` reached 22 users'
 * events between 2026-08-31 and 2026-10-26. Forms now carry `method="post"`
 * (guard: form-method-post.test.ts); this is the fence for any URL that still
 * arrives with a password-named param, on any path.
 */
export function redactCredentialParams(value: string): string {
  if (!value.toLowerCase().includes('password')) return value;
  try {
    const url = new URL(value);
    let hit = false;
    for (const key of [...url.searchParams.keys()]) {
      if (!key.toLowerCase().includes('password')) continue;
      url.searchParams.set(key, CODE_REDACTED);
      hit = true;
    }
    return hit ? url.toString() : value;
  } catch {
    return value;
  }
}

/**
 * Luhn check over 13-19 digits, the ISO/IEC 7812 range that covers Visa,
 * Mastercard, Amex, Discover and friends.
 */
function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let value = digits.charCodeAt(i) - 48;
    if (double) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
    double = !double;
  }
  return sum % 10 === 0;
}

export function looksLikePan(value: string): boolean {
  // Bail before Luhn on anything that cannot be a card — the common case.
  if (value.length < 13 || value.length > 25) return false;
  const digits = value.replace(/[\s-]/g, '');
  if (digits.length < 13 || digits.length > 19) return false;
  if (!/^\d+$/.test(digits)) return false;
  return passesLuhn(digits);
}

/**
 * `before_send` entry. Returning the event unchanged is the hot path; only a
 * card-shaped value, an OAuth callback code or a password param is rewritten.
 */
export function redactSensitiveProperties(event: CaptureResult | null): CaptureResult | null {
  if (!event?.properties) return event;
  for (const key of USER_TYPED_KEYS) {
    const value = event.properties[key];
    if (typeof value === 'string' && looksLikePan(value)) {
      event.properties[key] = PAN_REDACTED;
    }
  }
  redactUrls(event.properties);
  // A leaked URL that is a session's first page also rides on the person
  // profile ($initial_current_url) and on every later event ($session_entry_url).
  redactUrls(event.$set);
  redactUrls(event.$set_once);
  redactUrls(event.properties.$set);
  redactUrls(event.properties.$set_once);
  return event;
}

function redactUrls(record: unknown): void {
  if (!record || typeof record !== 'object') return;
  const props = record as Record<string, unknown>;
  for (const key of URL_KEYS) {
    const value = props[key];
    if (typeof value === 'string') props[key] = redactCredentialParams(redactOAuthCode(value));
  }
}
